import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageCircle, X, Send, Minimize2 } from 'lucide-react';
import apiClient from '../../utils/api';
import './ChatWidget.css';

// Simple Markdown renderer for chat messages
const renderMarkdown = (text) => {
  if (!text) return text;
  
  // Split by code blocks first to preserve them
  const parts = text.split(/(```[\s\S]*?```|`[^`]+`)/g);
  
  return parts.map((part, idx) => {
    // Code blocks
    if (part.startsWith('```') && part.endsWith('```')) {
      const code = part.slice(3, -3).trim();
      return <pre key={idx} className="code-block">{code}</pre>;
    }
    
    // Inline code
    if (part.startsWith('`') && part.endsWith('`')) {
      return <code key={idx} className="inline-code">{part.slice(1, -1)}</code>;
    }
    
    // Process regular text with bold and italic
    let processed = part;
    
    // Bold text: **text** or __text__
    const boldRegex = /(\*\*|__)(.*?)\1/g;
    const boldParts = [];
    let lastIndex = 0;
    let match;
    
    while ((match = boldRegex.exec(processed)) !== null) {
      if (match.index > lastIndex) {
        boldParts.push(processed.slice(lastIndex, match.index));
      }
      boldParts.push(<strong key={`bold-${idx}-${match.index}`}>{match[2]}</strong>);
      lastIndex = match.index + match[0].length;
    }
    
    if (lastIndex < processed.length) {
      boldParts.push(processed.slice(lastIndex));
    }
    
    return boldParts.length > 0 ? <span key={idx}>{boldParts}</span> : part;
  });
};

function ChatWidget() {
  const { t, i18n } = useTranslation();
  const isGerman = i18n.language === 'de';
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [usage, setUsage] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      loadChatHistory();
      loadUsage();
      markMessagesAsRead();
    } else {
      // Load unread count when widget is closed
      loadUnreadCount();
    }
  }, [isOpen]);

  // Poll for unread messages every 2 minutes
  useEffect(() => {
    if (!isOpen) {
      const interval = setInterval(() => {
        loadUnreadCount();
      }, 120000); // 2 minutes
      
      return () => clearInterval(interval);
    }
  }, [isOpen]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadChatHistory = async () => {
    try {
      const data = await apiClient.getChatHistory();
      setMessages(data.messages || []);
    } catch (error) {
      console.error('Error loading chat history:', error);
    }
  };

  const loadUsage = async () => {
    try {
      const data = await apiClient.getChatUsage();
      setUsage(data);
    } catch (error) {
      console.error('Error loading usage:', error);
    }
  };

  const loadUnreadCount = async () => {
    try {
      const data = await apiClient.getUnreadCount();
      setUnreadCount(data.count || 0);
    } catch (error) {
      console.error('Error loading unread count:', error);
    }
  };

  const markMessagesAsRead = async () => {
    try {
      await apiClient.markMessagesRead();
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || loading) return;

    const userMessage = inputMessage;
    setInputMessage('');
    setLoading(true);

    // Add user message immediately
    const tempUserMsg = {
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempUserMsg]);

    try {
      // Send message with current language
      const response = await apiClient.sendChatMessage(userMessage, i18n.language);
      
      // Add assistant response
      const assistantMsg = {
        role: 'assistant',
        content: response.message,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, assistantMsg]);
      
      // Update usage (reload to get latest coin balance)
      loadUsage();
    } catch (error) {
      console.error('Error sending message:', error);
      // Show error message
      const errorMsg = {
        role: 'assistant',
        content: t('companion.errorMessage', { error: error.message }),
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) {
    return (
      <button
        className="chat-widget-button"
        onClick={() => setIsOpen(true)}
        data-testid="chat-widget-button"
      >
        <MessageCircle size={24} />
        {unreadCount > 0 && (
          <span className="chat-widget-badge" data-testid="chat-unread-badge">
            {unreadCount}
          </span>
        )}
      </button>
    );
  }

  return (
    <div className={`chat-widget ${isMinimized ? 'minimized' : ''}`} data-testid="chat-widget">
      <div className="chat-widget-header">
        <div className="chat-widget-title">
          <MessageCircle size={20} />
          <span>{t('companion.title')}</span>
        </div>
        <div className="chat-widget-actions">
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            data-testid="chat-minimize-button"
          >
            <Minimize2 size={18} />
          </button>
          <button
            onClick={() => setIsOpen(false)}
            data-testid="chat-close-button"
          >
            <X size={18} />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          <div className="chat-widget-body">
            {messages.length === 0 ? (
              <div className="chat-welcome" data-testid="chat-welcome">
                <div className="chat-welcome-icon">✨</div>
                <h3>{t('companion.welcomeTitle')}</h3>
                <p>{t('companion.welcomeText')}</p>
                <p>{t('companion.welcomePrompt')}</p>
              </div>
            ) : (
              messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`chat-message ${msg.role}`}
                  data-testid={`chat-message-${msg.role}`}
                >
                  <div className="chat-message-content">
                    {renderMarkdown(msg.content)}
                  </div>
                  <div className="chat-message-time">
                    {new Date(msg.timestamp).toLocaleTimeString(isGerman ? 'de-DE' : 'en-US', {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                </div>
              ))
            )}
            {loading && (
              <div className="chat-message assistant" data-testid="chat-loading">
                <div className="chat-message-content">
                  <div className="chat-typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="chat-widget-footer">
            {usage && (
              <div className="chat-usage-info" style={{fontSize: '11px', opacity: 0.8}}>
                💰 {usage.coins_remaining} Coins {t('companion.costPerMessage', { cost: usage.coins_per_message })}
              </div>
            )}
            <form onSubmit={handleSendMessage} className="chat-input-form">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                placeholder={t('companion.placeholder')}
                disabled={loading}
                data-testid="chat-input"
              />
              <button
                type="submit"
                disabled={loading || !inputMessage.trim()}
                data-testid="chat-send-button"
              >
                <Send size={18} />
              </button>
            </form>
          </div>
        </>
      )}
    </div>
  );
}

export default ChatWidget;